"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

const DESKTOP_MEDIA_QUERY = "(min-width: 1440px)";

interface HeaderCompactContextValue {
  registerSentinel: (node: HTMLDivElement | null) => void;
}

const HeaderCompactContext = createContext<HeaderCompactContextValue | null>(
  null
);

export function useHeaderCompactContext(): HeaderCompactContextValue {
  const context = useContext(HeaderCompactContext);
  if (context === null) {
    throw new Error(
      "useHeaderCompactContext must be used within HeaderCompactProvider"
    );
  }
  return context;
}

interface HeaderCompactProviderProps {
  children: ReactNode;
}

export function HeaderCompactProvider({ children }: HeaderCompactProviderProps) {
  const [sentinelVisible, setSentinelVisible] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);
  const [sentinelNode, setSentinelNode] = useState<HTMLDivElement | null>(
    null
  );

  const registerSentinel = useCallback((node: HTMLDivElement | null) => {
    setSentinelNode(node);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);

    const updateDesktop = () => {
      setIsDesktop(mediaQuery.matches);
    };

    updateDesktop();
    mediaQuery.addEventListener("change", updateDesktop);

    return () => {
      mediaQuery.removeEventListener("change", updateDesktop);
    };
  }, []);

  useEffect(() => {
    if (!sentinelNode || !isDesktop) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setSentinelVisible(entry.isIntersecting);
      },
      { root: null, threshold: 0, rootMargin: "0px 0px 0px 0px" }
    );

    observer.observe(sentinelNode);

    return () => {
      observer.disconnect();
    };
  }, [sentinelNode, isDesktop]);

  const isCompact = isDesktop && sentinelNode !== null && !sentinelVisible;

  useEffect(() => {
    if (isCompact) {
      document.documentElement.dataset.headerCompact = "true";
    } else {
      delete document.documentElement.dataset.headerCompact;
    }

    return () => {
      delete document.documentElement.dataset.headerCompact;
    };
  }, [isCompact]);

  const contextValue = useMemo(
    () => ({
      registerSentinel,
    }),
    [registerSentinel]
  );

  return (
    <HeaderCompactContext.Provider value={contextValue}>
      {children}
    </HeaderCompactContext.Provider>
  );
}
